import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Diagnose Database Connection Issues',
    description: 'Systematically identify all database-related problems preventing WordPress from connecting to MySQL.',
    requirements: [
      {
        text: 'Check if MySQL service is running and accessible'
      },
      {
        text: 'Test database connectivity from command line'
      },
      {
        text: 'Review WordPress error logs for specific connection errors'
      },
      {
        text: 'Examine wp-config.php for database configuration'
      },
      {
        text: 'Verify network connectivity to database server'
      }
    ],
    verification: [
      'You have identified MySQL service status',
      'You know what error messages WordPress is showing',
      'You have located the wp-config.php file',
      'You understand which connection parameters are incorrect',
      'You have a clear list of issues to resolve'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Fix MySQL Service Issues',
    description: 'Ensure the MySQL database service is running properly and accepting connections.',
    requirements: [
      {
        text: 'Start MySQL service if it is not running'
      },
      {
        text: 'Enable MySQL to start automatically on boot'
      },
      {
        text: 'Verify MySQL is listening on the correct port:',
        subitems: ['Default port 3306', 'Check for custom port configurations']
      },
      {
        text: 'Check MySQL error logs for startup issues'
      },
      {
        text: 'Ensure MySQL socket file exists and has correct permissions'
      }
    ],
    verification: [
      'MySQL service is active and running',
      'MySQL is set to start on system boot',
      'Port 3306 is listening for connections',
      'No critical errors in MySQL logs',
      'MySQL accepts local connections'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Verify and Fix Database Credentials',
    description: 'Check and correct the database credentials used by WordPress to connect to MySQL.',
    requirements: [
      {
        text: 'Locate database credentials in wp-config.php'
      },
      {
        text: 'Test database connection with current credentials'
      },
      {
        text: 'Verify the following parameters:',
        subitems: [
          "DB_NAME matches actual database (required: 'wordpress_db')",
          "DB_USER exists in MySQL (required: 'wp_user')",
          "DB_PASSWORD is correct (required for grading: 'correctpassword123')",
          "DB_HOST is properly configured (required: 'localhost')"
        ]
      },
      {
        text: 'Update incorrect credentials in wp-config.php'
      },
      {
        text: 'Test connection with corrected credentials'
      }
    ],
    verification: [
      'Database name in wp-config.php matches existing database',
      'Database user credentials are verified',
      'Password authentication succeeds',
      'DB_HOST setting is correct (localhost or 127.0.0.1)',
      'Manual MySQL connection with credentials works'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Fix Database User Privileges',
    description: 'Ensure the WordPress database user has all necessary privileges to access and modify the database.',
    requirements: [
      {
        text: 'Log into MySQL as root or administrative user'
      },
      {
        text: 'Check current privileges for WordPress database user'
      },
      {
        text: 'Grant necessary privileges on WordPress database:',
        subitems: ['ALL PRIVILEGES on wordpress database', 'Ensure user can connect from correct host', 'Apply privilege changes with FLUSH PRIVILEGES']
      },
      {
        text: 'Verify user can create, read, update, and delete data'
      },
      {
        text: 'Test WordPress database operations'
      }
    ],
    verification: [
      'WordPress user has ALL PRIVILEGES on its database',
      'User can connect from localhost or configured host',
      'FLUSH PRIVILEGES has been executed',
      'User can perform all CRUD operations',
      'No permission denied errors occur'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Verify Database Tables',
    description: 'Check that all required WordPress database tables exist and are accessible.',
    requirements: [
      {
        text: 'Check if WordPress database exists and is accessible'
      },
      {
        text: 'Verify all WordPress tables are present'
      },
      {
        text: 'Check for corrupted tables using MySQL CHECK TABLE'
      },
      {
        text: 'If tables are missing, trigger WordPress installation:',
        subitems: ['Access WordPress URL to start installation', 'Or use WP-CLI to install core tables', 'Ensure wp_options, wp_posts, and wp_users tables exist', 'Create an admin user during installation']
      },
      {
        text: 'Verify table permissions and ownership'
      }
    ],
    verification: [
      'WordPress database contains all required tables',
      'No table corruption errors are found',
      'Tables are accessible by WordPress user',
      'wp_options table contains siteurl setting',
      'At least one WordPress admin/user account exists',
      'Database structure matches WordPress requirements'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Final Verification',
    description: 'Confirm WordPress is fully functional with database connectivity restored.',
    requirements: [
      {
        text: 'Test WordPress homepage loads without errors'
      },
      {
        text: 'Verify no "Error establishing database connection" message'
      },
      {
        text: 'Check Apache error logs for any remaining issues'
      },
      {
        text: 'Test WordPress admin login functionality'
      },
      {
        text: 'Verify all services remain stable after fixes'
      }
    ],
    verification: [
      'WordPress homepage loads successfully',
      'No database connection errors displayed',
      'Admin dashboard is accessible',
      'All required services are running',
      'WordPress can read and write to database'
    ]
  }
];

export default function WordPressDatabaseConnectionFailureInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className={`px-4 py-[6px] text-xs font-medium rounded-md transition-all ${
              currentTaskIndex === 0
                ? 'bg-[#2d3748] text-[#64748b] cursor-not-allowed'
                : 'bg-[#475569] text-[#e2e8f0] hover:bg-[#64748b]'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-[6px] bg-[#10b981] text-[#1a1f2e] text-xs font-medium rounded-md hover:bg-[#059669] transition-all"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Start Over' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
